import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import { formatCapabilityLabel } from '../utils/capabilityContract';
import {
  buildMyWork,
  buildMyWorkNavigation,
  canContainPersonalWork,
  filterMyTasks,
  filterMyWorkstreams,
  getCurrentUserId,
} from '../utils/myWork';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const PAGE_SIZE = 200;
const DETAIL_CONCURRENCY = 6;

const statusColor = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'completed') return 'success';
  if (['failed', 'cancelled', 'declined'].includes(normalized)) return 'error';
  if (normalized === 'waiting') return 'warning';
  if (normalized === 'in_progress') return 'info';
  if (normalized === 'accepted') return 'primary';
  return 'default';
};

const formatDate = (value, options = {}) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  });
};

const formatRelativeTime = (value) => {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  const seconds = Math.round((parsed.getTime() - Date.now()) / 1000);
  const absoluteSeconds = Math.abs(seconds);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (absoluteSeconds < 60) return formatter.format(seconds, 'second');
  if (absoluteSeconds < 3600) return formatter.format(Math.round(seconds / 60), 'minute');
  if (absoluteSeconds < 86400) return formatter.format(Math.round(seconds / 3600), 'hour');
  if (absoluteSeconds < 2592000) return formatter.format(Math.round(seconds / 86400), 'day');
  return formatDate(value);
};

async function fetchIncomingSummaries(signal) {
  const byId = new Map();
  let offset = 0;

  while (true) {
    const response = await axiosInstance.get('/requests/direct', {
      params: { view: 'incoming', limit: PAGE_SIZE, offset },
      signal,
    });
    const page = Array.isArray(response.data?.requests) ? response.data.requests : [];
    const previousSize = byId.size;
    page.forEach((item) => {
      if (item?.request_capability_id) byId.set(item.request_capability_id, item);
    });

    offset += page.length;
    const total = Number(response.data?.pagination?.total);
    if (
      !page.length ||
      byId.size === previousSize ||
      (Number.isFinite(total) && offset >= total) ||
      (!Number.isFinite(total) && page.length < PAGE_SIZE)
    ) {
      break;
    }
  }

  return [...byId.values()];
}

async function fetchDetailsWithLimit(summaries, signal) {
  const results = new Array(summaries.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < summaries.length) {
      const index = nextIndex;
      nextIndex += 1;
      const summary = summaries[index];
      try {
        const response = await axiosInstance.get(
          `/requests/direct/${encodeURIComponent(summary.request_capability_id)}`,
          { signal },
        );
        results[index] = { detail: response.data || null, error: false };
      } catch (error) {
        results[index] = { detail: null, error: true };
      }
    }
  }

  const workerCount = Math.min(DETAIL_CONCURRENCY, summaries.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

const uniqueOptions = (items, idKey, labelKey) => {
  const options = new Map();
  items.forEach((item) => {
    const id = item[idKey];
    if (id) options.set(id, item[labelKey]);
  });
  return [...options.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((left, right) => left.label.localeCompare(right.label));
};

function MetadataItem({ label, value, icon }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex', pt: 0.2 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 650, wordBreak: 'break-word' }}>{value}</Typography>
      </Box>
    </Stack>
  );
}

function WorkstreamCard({ item, onOpen }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 850, wordBreak: 'break-word' }}>
                {item.capabilityName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {item.requestTitle}
              </Typography>
            </Box>
            <Chip size="small" color={statusColor(item.status)} label={formatCapabilityLabel(item.status)} />
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
            <MetadataItem label="Provider Business Unit" value={item.providerBusinessUnitLabel} icon={<BusinessRoundedIcon fontSize="small" />} />
            <MetadataItem label="Requested By" value={item.requestingBusinessUnitLabel} icon={<BusinessRoundedIcon fontSize="small" />} />
            <MetadataItem label="Claimed" value={formatDate(item.claimedAt)} icon={<WorkRoundedIcon fontSize="small" />} />
            <MetadataItem
              label="Tasks"
              value={`${item.openTaskCount} open${item.totalTaskCount !== item.openTaskCount ? ` · ${item.totalTaskCount} total` : ''}`}
              icon={<AssignmentTurnedInRoundedIcon fontSize="small" />}
            />
          </Box>

          <Divider />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Updated {formatRelativeTime(item.updatedAt)}
            </Typography>
            <Button
              variant="contained"
              size="small"
              endIcon={<OpenInNewRoundedIcon />}
              onClick={() => onOpen(item)}
              disabled={!item.workspaceId}
            >
              Open Workstream
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function TaskCard({ item, onOpen }) {
  const output = [item.expectedOutputName, item.expectedOutputKind && formatCapabilityLabel(item.expectedOutputKind)]
    .filter(Boolean)
    .join(' · ');
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={1.75}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 850, wordBreak: 'break-word' }}>{item.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {item.workstreamName} · {item.requestTitle}
              </Typography>
            </Box>
            <Chip size="small" color={statusColor(item.status)} label={formatCapabilityLabel(item.status)} />
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
            <MetadataItem label="Business Unit" value={item.providerBusinessUnitLabel} icon={<BusinessRoundedIcon fontSize="small" />} />
            <MetadataItem label="Created By" value={item.createdByLabel} icon={<AssignmentTurnedInRoundedIcon fontSize="small" />} />
            {output ? <MetadataItem label="Expected Output" value={output} icon={<WorkRoundedIcon fontSize="small" />} /> : null}
            {item.dueAt ? <MetadataItem label="Due" value={formatDate(item.dueAt)} icon={<CalendarMonthRoundedIcon fontSize="small" />} /> : null}
          </Box>

          <Divider />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Updated {formatRelativeTime(item.updatedAt)}
            </Typography>
            <Button
              variant="contained"
              size="small"
              endIcon={<OpenInNewRoundedIcon />}
              onClick={() => onOpen(item)}
              disabled={!item.workspaceId}
            >
              Open Task
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function EmptyState({ kind, hasAny, onViewRequests }) {
  const workstreams = kind === 'workstreams';
  const title = hasAny
    ? `No ${workstreams ? 'workstreams' : 'tasks'} match these filters`
    : workstreams ? 'No claimed workstreams' : 'No tasks assigned to you';
  const description = hasAny
    ? 'Try changing the status, context, or search filters.'
    : workstreams
      ? 'Capability workstreams you claim will appear here.'
      : 'Tasks assigned to you from capability workstreams will appear here.';
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderStyle: 'dashed' }}>
      <CardContent>
        <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ py: 5 }}>
          {workstreams ? <WorkRoundedIcon color="disabled" sx={{ fontSize: 44 }} /> : <AssignmentTurnedInRoundedIcon color="disabled" sx={{ fontSize: 44 }} />}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{description}</Typography>
          </Box>
          {workstreams && !hasAny ? (
            <Button variant="outlined" onClick={onViewRequests}>View Incoming Requests</Button>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function MyWorkPage(props) {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const partnerProfile = useSelector((state) => state.auth.partnerProfile);
  const currentUserId = getCurrentUserId(user, partnerProfile);
  const [retryKey, setRetryKey] = useState(0);
  const [tab, setTab] = useState('workstreams');
  const [search, setSearch] = useState('');
  const [workstreamView, setWorkstreamView] = useState('active');
  const [taskView, setTaskView] = useState('active');
  const [workstreamBusinessUnit, setWorkstreamBusinessUnit] = useState('');
  const [taskBusinessUnit, setTaskBusinessUnit] = useState('');
  const [capabilityFilter, setCapabilityFilter] = useState('');
  const [taskWorkstreamFilter, setTaskWorkstreamFilter] = useState('');
  const [loadState, setLoadState] = useState({
    loading: true,
    error: '',
    warning: '',
    workstreams: [],
    tasks: [],
  });

  useEffect(() => {
    if (!currentUserId) {
      setLoadState({
        loading: false,
        error: 'Unable to determine your account identity.',
        warning: '',
        workstreams: [],
        tasks: [],
      });
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    setLoadState((current) => ({ ...current, loading: true, error: '', warning: '' }));

    async function load() {
      try {
        const summaries = await fetchIncomingSummaries(controller.signal);
        const candidates = summaries.filter(canContainPersonalWork);
        const detailResults = await fetchDetailsWithLimit(candidates, controller.signal);
        if (!active || controller.signal.aborted) return;

        const details = detailResults.map((result) => result?.detail).filter(Boolean);
        const failedCount = detailResults.filter((result) => result?.error).length;
        if (candidates.length && failedCount === candidates.length) {
          setLoadState({
            loading: false,
            error: 'Unable to load your work.',
            warning: '',
            workstreams: [],
            tasks: [],
          });
          return;
        }

        const personalWork = buildMyWork(details, currentUserId);
        setLoadState({
          loading: false,
          error: '',
          warning: failedCount
            ? `${failedCount} workstream${failedCount === 1 ? '' : 's'} could not be loaded. These results may be incomplete.`
            : '',
          ...personalWork,
        });
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        setLoadState({
          loading: false,
          error: 'Unable to load your work.',
          warning: '',
          workstreams: [],
          tasks: [],
        });
      }
    }

    load();
    return () => {
      active = false;
      controller.abort();
    };
  }, [currentUserId, retryKey]);

  const filteredWorkstreams = useMemo(() => filterMyWorkstreams(loadState.workstreams, {
    view: workstreamView,
    search,
    businessUnitId: workstreamBusinessUnit,
    capabilityDefinitionId: capabilityFilter,
  }), [loadState.workstreams, workstreamView, search, workstreamBusinessUnit, capabilityFilter]);

  const filteredTasks = useMemo(() => filterMyTasks(loadState.tasks, {
    view: taskView,
    search,
    businessUnitId: taskBusinessUnit,
    workstreamId: taskWorkstreamFilter,
  }), [loadState.tasks, taskView, search, taskBusinessUnit, taskWorkstreamFilter]);

  const workstreamBusinessUnits = useMemo(
    () => uniqueOptions(loadState.workstreams, 'providerBusinessUnitId', 'providerBusinessUnitLabel'),
    [loadState.workstreams],
  );
  const taskBusinessUnits = useMemo(
    () => uniqueOptions(loadState.tasks, 'providerBusinessUnitId', 'providerBusinessUnitLabel'),
    [loadState.tasks],
  );
  const capabilities = useMemo(
    () => uniqueOptions(loadState.workstreams, 'capabilityDefinitionId', 'capabilityName'),
    [loadState.workstreams],
  );
  const taskWorkstreams = useMemo(
    () => uniqueOptions(loadState.tasks, 'requestCapabilityId', 'workstreamName'),
    [loadState.tasks],
  );

  const openItem = (item) => {
    const target = buildMyWorkNavigation(item);
    if (target) navigate(target.path, { state: target.state });
  };

  const statusView = tab === 'workstreams' ? workstreamView : taskView;
  const setStatusView = tab === 'workstreams' ? setWorkstreamView : setTaskView;
  const items = tab === 'workstreams' ? filteredWorkstreams : filteredTasks;
  const hasAny = tab === 'workstreams' ? Boolean(loadState.workstreams.length) : Boolean(loadState.tasks.length);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: 3, mt: { xs: 7, md: 0 } }}>
          <Header breadcrumbItems={[{ label: 'AMX xConsole', href: '/' }, { label: 'My Work' }]} />
          <Stack spacing={3} sx={{ maxWidth: 1500, mx: 'auto' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 850 }}>My Work</Typography>
              <Typography variant="body1" color="text.secondary">
                Workstreams and tasks assigned to you.
              </Typography>
            </Box>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <Tabs
                value={tab}
                onChange={(_event, value) => setTab(value)}
                aria-label="My Work views"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab value="workstreams" label={`Workstreams (${loadState.workstreams.length})`} />
                <Tab value="tasks" label={`Tasks (${loadState.tasks.length})`} />
              </Tabs>
              <Divider />
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between">
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={statusView}
                      onChange={(_event, value) => value && setStatusView(value)}
                      aria-label={`${tab} status filter`}
                    >
                      <ToggleButton value="active">{tab === 'workstreams' ? 'Active' : 'Open'}</ToggleButton>
                      <ToggleButton value="completed">Completed</ToggleButton>
                      <ToggleButton value="all">All</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                      size="small"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search your work"
                      aria-label="Search your work"
                      sx={{ width: { xs: '100%', lg: 360 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment>
                        ),
                      }}
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                    {tab === 'workstreams' ? (
                      <>
                        <TextField
                          select
                          size="small"
                          label="Business Unit"
                          value={workstreamBusinessUnit}
                          onChange={(event) => setWorkstreamBusinessUnit(event.target.value)}
                          sx={{ minWidth: { xs: '100%', sm: 240 } }}
                        >
                          <MenuItem value="">All Business Units</MenuItem>
                          {workstreamBusinessUnits.map((option) => <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>)}
                        </TextField>
                        <TextField
                          select
                          size="small"
                          label="Capability"
                          value={capabilityFilter}
                          onChange={(event) => setCapabilityFilter(event.target.value)}
                          sx={{ minWidth: { xs: '100%', sm: 240 } }}
                        >
                          <MenuItem value="">All Capabilities</MenuItem>
                          {capabilities.map((option) => <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>)}
                        </TextField>
                      </>
                    ) : (
                      <>
                        <TextField
                          select
                          size="small"
                          label="Business Unit"
                          value={taskBusinessUnit}
                          onChange={(event) => setTaskBusinessUnit(event.target.value)}
                          sx={{ minWidth: { xs: '100%', sm: 240 } }}
                        >
                          <MenuItem value="">All Business Units</MenuItem>
                          {taskBusinessUnits.map((option) => <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>)}
                        </TextField>
                        <TextField
                          select
                          size="small"
                          label="Workstream"
                          value={taskWorkstreamFilter}
                          onChange={(event) => setTaskWorkstreamFilter(event.target.value)}
                          sx={{ minWidth: { xs: '100%', sm: 240 } }}
                        >
                          <MenuItem value="">All Workstreams</MenuItem>
                          {taskWorkstreams.map((option) => <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>)}
                        </TextField>
                      </>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {loadState.warning ? <Alert severity="warning">{loadState.warning}</Alert> : null}
            {loadState.error ? (
              <Alert
                severity="error"
                action={(
                  <Button color="inherit" size="small" startIcon={<ReplayRoundedIcon />} onClick={() => setRetryKey((value) => value + 1)}>
                    Retry
                  </Button>
                )}
              >
                {loadState.error}
              </Alert>
            ) : null}

            {loadState.loading ? (
              <Stack spacing={1.5} alignItems="center" sx={{ py: 8 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">Loading your work...</Typography>
              </Stack>
            ) : null}

            {!loadState.loading && !loadState.error ? (
              items.length ? (
                <Stack spacing={2}>
                  {tab === 'workstreams'
                    ? items.map((item) => <WorkstreamCard key={item.requestCapabilityId} item={item} onOpen={openItem} />)
                    : items.map((item) => <TaskCard key={item.taskId} item={item} onOpen={openItem} />)}
                </Stack>
              ) : (
                <EmptyState
                  kind={tab}
                  hasAny={hasAny}
                  onViewRequests={() => navigate('/requests', { state: { view: 'incoming' } })}
                />
              )
            ) : null}
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
